import { createStore, StoreCore } from "@priolo/jon"
import i18n from "i18next"
import dialogSo from "stores/layout/dialogStore"
import { moveTo } from "utils/array"
import { getNextPhase, ListPhases, PHASE_SLUG } from "../../phase/utils"
import { CombinedDay, ExtendedGroupPhase } from "./types"
import { getNewDay, getNewGroupPhase, isRealPhase } from "./utils"



/** 
 * Si occupa della finestra dettaglio del CROP 
 * */
const setup = {

	state: {
		daysGroups: <ExtendedGroupPhase[]>[],
		startingDay: 0,
		common: <Partial<CombinedDay>>{},
		clipboard: <CombinedDay[]>[],
	},

	getters: {
		getAllSelect: (_: void, store?: CropRecipeDayStore): CombinedDay[] => store.state.daysGroups
			.reduce<CombinedDay[]>((acc, dg) => {
				return acc.concat(dg.days.filter((d, dIndex) => dg.daysSelect.includes(dIndex)))
			}, []),
		havePreCycle: (_: void, store?: CropRecipeDayStore) => {
			return store.state.daysGroups[0]?.phase == PHASE_SLUG.PRE_CYCLE
		},
		havePostCycle: (_: void, store?: CropRecipeDayStore) => {
			return store.state.daysGroups[store.state.daysGroups.length - 1]?.phase == PHASE_SLUG.POST_CYCLE
		},
		checkRatio: (index: number, store?: CropRecipeDayStore) => {
			const group = store.state.daysGroups[index]
			if (!group) return false
			if (!isRealPhase(group.phase)) return true
			const groupRatio = +group.ratio
			if (index == 0 && groupRatio == 1) return true
			const groupPrev = store.state.daysGroups[index - 1]
			const groupPrevRatio = +groupPrev.ratio
			if (!isRealPhase(groupPrev.phase) && groupRatio == 1) return true
			if ((groupRatio % groupPrevRatio) == 0) return true
			return false
		},
		checkElasticPhase: (value: number, store?: CropRecipeDayStore) => {
			if (value < 0 || value > 7) {
				return false
			}
			return true
		},
		checkAllRatio: (_: void, store?: CropRecipeDayStore) => {
			const check = store.state.daysGroups.every((group, index) => store.checkRatio(index))
			return check
		},
	},

	actions: {
		clearSelection: (groupIndex?: number, store?: CropRecipeDayStore) => {
			if (groupIndex != null) {
				store.state.daysGroups[groupIndex].daysSelect = []
			} else {
				store.state.daysGroups.forEach(g => g.daysSelect = [])
			}
			store.setDaysGroups([...store.state.daysGroups])
		},
		selectAll: (groupIndex?: number, store?: CropRecipeDayStore) => {
			store.state.daysGroups.forEach((g, index) => {
				if (groupIndex != null && groupIndex != index) return
				g.daysSelect = Array.from({ length: g.days.length }, (x, i) => i)
			})
			store.setDaysGroups([...store.state.daysGroups])
		},

		addPhase: (groupIndex: number, store?: CropRecipeDayStore) => {
			const daysGroups = [...store.state.daysGroups]
			const newPhase = getNextPhase({ used: daysGroups.map(g => g.phase) })
			const newGroup = getNewGroupPhase(newPhase)
			daysGroups.splice(groupIndex + 1, 0, newGroup)
			store.setDaysGroups(daysGroups)
		},
		movePhase: ({ from, to }: { from: number, to: number }, store?: CropRecipeDayStore) => {
			const daysGroups: ExtendedGroupPhase[] = [...store.state.daysGroups]
			store.setDaysGroups(moveTo(daysGroups, from, to))
		},
		addPrePhase: (_: void, store?: CropRecipeDayStore) => {
			const daysGroups = [...store.state.daysGroups]
			const newGroup = getNewGroupPhase(PHASE_SLUG.PRE_CYCLE)
			daysGroups.splice(0, 0, newGroup)
			store.setDaysGroups(daysGroups)
		},
		addPostPhase: (_: void, store?: CropRecipeDayStore) => {
			const daysGroups = [...store.state.daysGroups]
			const newGroup = getNewGroupPhase(PHASE_SLUG.POST_CYCLE)
			daysGroups.push(newGroup)
			store.setDaysGroups(daysGroups)
		},

		//#region DAY
		addDay: (groupIndex: number, store?: CropRecipeDayStore) => {
			const groups = [...store.state.daysGroups]
			const days = groups[groupIndex].days
			// prendo in cosiderazione il DAY precedente per mantenere le stesse caratteristiche
			days.push(getNewDay(days[days.length - 1]))
			store.setDaysGroups(groups)
		},
		deleteDay: (
			{ groupIndex, dayIndex }: { groupIndex: number, dayIndex: number },
			store?: CropRecipeDayStore
		) => {
			const groups = [...store.state.daysGroups]
			const group = groups[groupIndex]
			if (group.days.length == 1) {
				dialogSo.dialogOpen({
					title: i18n.t(`dialog.crop-recipe.delete-last-day.title`),
					text: i18n.t(`dialog.crop-recipe.delete-last-day.text`),
				})
				return
			}
			group.days.splice(dayIndex, 1)
			store.setDaysGroups(groups)
		},
		changeDay: (
			{ newDay, groupIndex, dayIndex }: { newDay: CombinedDay, groupIndex: number, dayIndex: number },
			store?: CropRecipeDayStore
		) => {
			const groups = [...store.state.daysGroups]
			groups[groupIndex].days[dayIndex] = newDay
			store.setDaysGroups(groups)
		},
		cloneDay: (
			{ groupIndex, dayIndex }: { groupIndex: number, dayIndex: number },
			store?: CropRecipeDayStore
		) => {
			const groups = [...store.state.daysGroups]
			const dayClone = { ...groups[groupIndex].days[dayIndex] }
			groups[groupIndex].days.splice(dayIndex, 0, dayClone)
			store.setDaysGroups(groups)
		},
		dragEndDay: (result, store?: CropRecipeDayStore) => {
			if (!result.destination) return
			const groups = [...store.state.daysGroups]
			const groupIndex = +result.destination.droppableId
			const group = groups[groupIndex]

			const [del] = group.days.splice(result.source.index, 1)
			group.days.splice(result.destination.index, 0, del)
			store.setDaysGroups(groups)
		},
		//#endregion

		//#region GROUP PHASE
		changeGroup: (
			{ newGroup, groupIndex }: { newGroup: ExtendedGroupPhase, groupIndex: number },
			store?: CropRecipeDayStore
		) => {
			const groups = [...store.state.daysGroups]
			groups[groupIndex] = newGroup
			store.setDaysGroups(groups)
		},
		deleteGroup: (groupIndex: number, store?: CropRecipeDayStore) => {
			const groups = store.state.daysGroups.filter((g, i) => i != groupIndex)
			if (groups.length == 0) {
				dialogSo.dialogOpen({
					title: i18n.t(`dialog.crop-recipe.delete-last-phase.title`),
					text: i18n.t(`dialog.crop-recipe.delete-last-phase.text`),
				})
				return
			}
			store.setDaysGroups(groups)
		},
		cloneGroup: (groupIndex: number, store?: CropRecipeDayStore) => {
			const groups = [...store.state.daysGroups]
			const cloneGroup = JSON.parse(JSON.stringify(groups[groupIndex]))
			groups.splice(groupIndex, 0, cloneGroup)
			store.setDaysGroups(groups)
		},
		//#endregion
	},

	mutators: {
		setDaysGroups: (daysGroups: ExtendedGroupPhase[]) => ({ daysGroups }),
		setNewDaysGroups: () => ({
			daysGroups: <ExtendedGroupPhase[]>[
				{
					phase: ListPhases[0],
					collapsed: false,
					days: [getNewDay()],
					daysSelect: [],
					ratio: 1,
				}
			]
		}),
		setStartingDay: (startingDay: number) => {
			if (startingDay < 0) startingDay = 7 + startingDay
			return { startingDay: startingDay % 7 }
		},

		setCommon: (common: Partial<CombinedDay>) => ({ common }),
		setCommonProp: (prop: Partial<CombinedDay>, store?: CropRecipeDayStore) => ({ common: { ...store.state.common, ...prop } }),
		setClipboard: (clipboard: CombinedDay[]) => ({ clipboard }),

		/** setta il valore per tutte le righe selezionate */
		setDaysRowsSelected: (prop: Partial<ExtendedGroupPhase>, store?: CropRecipeDayStore) => {
			for ( let groupIndex = 0; groupIndex < store.state.daysGroups.length; groupIndex++ ) {
				const group = store.state.daysGroups[groupIndex]
				for ( const dayIndex of group.daysSelect ) {
					store.setDayRow({ groupIndex, dayIndex, prop })
				}
			}
		},
		/** setta il valore solo alla riga indicata */
		setDayRow: ({ groupIndex, dayIndex, prop }: { groupIndex: number, dayIndex: number, prop: Partial<ExtendedGroupPhase> }, store?: CropRecipeDayStore) => {
			const group = store.state.daysGroups[groupIndex]
			const day = group.days[dayIndex]
			const newDay = { ...day, ...prop }
			group.days[dayIndex] = newDay
			store.setDaysGroups([...store.state.daysGroups])
		}
	},
}

export type CropRecipeDayState = typeof setup.state
export type CropRecipeDayGetters = typeof setup.getters
export type CropRecipeDayActions = typeof setup.actions
export type CropRecipeDayMutators = typeof setup.mutators
export interface CropRecipeDayStore extends StoreCore<CropRecipeDayState>, CropRecipeDayGetters, CropRecipeDayActions, CropRecipeDayMutators {
	state: CropRecipeDayState
}

/**
 * Store per la gestione dei DAYS di un CROP-RECIPE
 * questi DAY sono ricavati dalle fasi del CROP e del RECIPE
 * per la gestione della UI di dettaglio
 */
const cropRecipeDaySo = createStore(setup) as CropRecipeDayStore
export default cropRecipeDaySo

